import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

interface Message {
  message: string;
  timestamp: string;
}

interface WeatherData {
  city: string;
  temperature: number;
  condition: string;
}

interface UserInfo {
  authenticated: boolean;
  userId?: string;
  userDetails?: string;
  identityProvider?: string;
  userRoles?: string[];
  claims?: any;
  message?: string;
  error?: string;
}

interface GitHubOrganization {
  login: string;
  id: number;
  avatar_url?: string;
  description?: string;
}

interface OrganizationsData {
  authenticated: boolean;
  organizations?: GitHubOrganization[];
  message?: string;
  error?: string;
}

function App() {
  const [message, setMessage] = useState<Message | null>(null);
  const [weather, setWeather] = useState<WeatherData[]>([]);
  const [loading, setLoading] = useState(false);
  const [postResult, setPostResult] = useState<any>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationsData | null>(null);

  const fetchMessage = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/message');
      const data = await response.json();
      setMessage(data);
    } catch (error) {
      console.error('Error fetching message:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeather = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/weather');
      const data = await response.json();
      setWeather(data);
    } catch (error) {
      console.error('Error fetching weather:', error);
    } finally {
      setLoading(false);
    }
  };

  const postData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          test: 'Hello from React!', 
          timestamp: new Date().toISOString() 
        }),
      });
      const data = await response.json();
      setPostResult(data);
    } catch (error) {
      console.error('Error posting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await fetch('/api/secure/github/organizations');
      const data = await response.json();
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations({
        authenticated: false,
        error: 'Failed to fetch organizations'
      });
    }
  }, []);

  const fetchUserInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/secure/userinfo');
      const data = await response.json();
      setUserInfo(data);
      
      // If user is authenticated, also fetch their GitHub organizations
      if (data.authenticated) {
        fetchOrganizations();
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserInfo({
        authenticated: false,
        error: 'Failed to fetch user information'
      });
    } finally {
      setLoading(false);
    }
  }, [fetchOrganizations]);

  const loginWithGitHub = () => {
    window.location.href = '/.auth/login/github';
  };

  const loginWithFacebook = () => {
    window.location.href = '/.auth/login/facebook?post_login_redirect_uri=https://swa-poc.autostract.com/';
  };

  const loginWithAuth0 = () => {
    window.location.href = '/.auth/login/auth0';
  };

  const logout = () => {
    window.location.href = '/.auth/logout';
  };

  useEffect(() => {
    fetchMessage();
    fetchUserInfo();
  }, [fetchUserInfo]);

  return (
    <div className="App">
      <header className="App-header">
        <h1>Azure Static Web App POC</h1>
        <p>React Frontend with .NET Core Azure Functions Backend</p>
        
        <div style={{ margin: '20px', textAlign: 'left', maxWidth: '600px' }}>
          {/* Authentication Section */}
          <section style={{ marginBottom: '30px', border: '1px solid #444', padding: '20px', borderRadius: '5px' }}>
            <h2>Authentication</h2>
            {userInfo ? (
              userInfo.authenticated ? (
                <div style={{ backgroundColor: '#2a4d3a', padding: '15px', borderRadius: '5px', marginBottom: '10px' }}>
                  <p><strong>✅ Authenticated as:</strong> {userInfo.userDetails || userInfo.userId}</p>
                  <p><strong>Provider:</strong> {userInfo.identityProvider}</p>
                  {userInfo.userRoles && userInfo.userRoles.length > 0 && (
                    <p><strong>Roles:</strong> {userInfo.userRoles.join(', ')}</p>
                  )}
                  
                  {/* Claims Display */}
                  {userInfo.claims && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #4d6d4d' }}>
                      <p><strong>Claims:</strong></p>
                      <div style={{ backgroundColor: '#1a2f1a', padding: '10px', borderRadius: '4px', marginTop: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                        <pre style={{ margin: 0, fontSize: '0.85em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {JSON.stringify(userInfo.claims, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {/* GitHub Organizations */}
                  {organizations && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #4d6d4d' }}>
                      <p><strong>GitHub Organizations:</strong></p>
                      {organizations.organizations && organizations.organizations.length > 0 ? (
                        <div style={{ marginTop: '10px' }}>
                          {organizations.organizations.map((org) => (
                            <div key={org.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', backgroundColor: '#1a2f1a', borderRadius: '4px', marginBottom: '8px' }}>
                              {org.avatar_url && (
                                <img src={org.avatar_url} alt={org.login} style={{ width: '32px', height: '32px', borderRadius: '4px', marginRight: '10px' }} />
                              )}
                              <div>
                                <div style={{ fontWeight: 'bold' }}>{org.login}</div>
                                {org.description && <div style={{ fontSize: '0.9em', color: '#aaa' }}>{org.description}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontStyle: 'italic', color: '#aaa' }}>
                          {organizations.message || 'No organizations found'}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <button onClick={logout} style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Logout
                  </button>
                </div>
              ) : (
                <div style={{ backgroundColor: '#4d2a2a', padding: '15px', borderRadius: '5px', marginBottom: '10px' }}>
                  <p><strong>❌ Not authenticated</strong></p>
                  {userInfo.message && <p>{userInfo.message}</p>}
                  {userInfo.error && <p>Error: {userInfo.error}</p>}
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button onClick={loginWithGitHub} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Login with GitHub
                    </button>
                    <button onClick={loginWithFacebook} style={{ padding: '8px 16px', backgroundColor: '#1877f2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Login with Facebook
                    </button>
                    <button onClick={loginWithAuth0} style={{ padding: '8px 16px', backgroundColor: '#eb5424', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Login with Auth0
                    </button>
                  </div>
                </div>
              )
            ) : (
              <p>Loading authentication status...</p>
            )}
            <button onClick={fetchUserInfo} disabled={loading} style={{ marginTop: '10px', marginLeft: '10px' }}>
              Refresh Auth Status
            </button>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2>Message from Azure Functions</h2>
            {message ? (
              <div style={{ backgroundColor: '#333', padding: '10px', borderRadius: '5px' }}>
                <p><strong>Message:</strong> {message.message}</p>
                <p><strong>Timestamp:</strong> {new Date(message.timestamp).toLocaleString()}</p>
              </div>
            ) : (
              <p>Loading message...</p>
            )}
            <button onClick={fetchMessage} disabled={loading} style={{ marginTop: '10px' }}>
              Refresh Message
            </button>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2>Weather Data</h2>
            <button onClick={fetchWeather} disabled={loading} style={{ marginBottom: '10px' }}>
              Get Weather Data
            </button>
            {weather.length > 0 && (
              <div style={{ backgroundColor: '#333', padding: '10px', borderRadius: '5px' }}>
                {weather.map((item, index) => (
                  <div key={index} style={{ marginBottom: '5px' }}>
                    <strong>{item.city}:</strong> {item.temperature}°C, {item.condition}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2>POST Data Test</h2>
            <button onClick={postData} disabled={loading} style={{ marginBottom: '10px' }}>
              Send POST Request
            </button>
            {postResult && (
              <div style={{ backgroundColor: '#333', padding: '10px', borderRadius: '5px' }}>
                <p><strong>Received:</strong> {postResult.received}</p>
                <p><strong>Processed:</strong> {postResult.processed ? 'Yes' : 'No'}</p>
                <p><strong>Response Timestamp:</strong> {new Date(postResult.timestamp).toLocaleString()}</p>
              </div>
            )}
          </section>

          {loading && <p>Loading...</p>}
          
          <footer style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #444', fontSize: '0.9em' }}>
            <Link to="/privacy" style={{ color: '#61dafb', textDecoration: 'none' }}>Privacy Policy</Link>
          </footer>
        </div>
      </header>
    </div>
  );
}

export default App;
